<template>
  <q-page class="main-wrapper-page single-page" style="background-color: var(--q-accent)">
    <section class="page-section q-mb-lg">
      <q-form ref="form" class="full-width">
        <div class="row q-col-gutter-md">
          <div class="col-6">
            <q-input
              v-model="formData.apmid"
              :rules="[(val) => !!val || 'Field is required']"
              outlined
              dense
              :readonly="isEditMode"
              label="APM ID *"
              class="bg-white full-width"
              @update:model-value="onFormChange"
            />
          </div>
          <div class="col-6">
            <q-select
              v-model="formData.status"
              :rules="[(val) => !!val || 'Field is required']"
              label="Status *"
              outlined
              dense
              options-dense
              :options="[
                { label: 'Draft', value: 'draft' },
                { label: 'Development', value: 'development' },
                { label: 'Production', value: 'production' },
              ]"
              clearable
              emit-value
              class="bg-white full-width"
              @update:model-value="onFormChange"
            />
          </div>
        </div>
      </q-form>
    </section>

    <section class="page-section">
      <div class="flex items-center q-mb-md">
        <span>Dark theme</span>
        <q-toggle false-value="vs-dark" class="q-mx-sm" true-value="vs" v-model="theme" />
        <span>Light theme</span>
      </div>

      <div class="row q-col-gutter-md">
        <div class="col-12">
          <q-card flat bordered class="full-height">
            <q-card-section class="q-pb-none">
              <div class="text-subtitle1 text-weight-bold">Manifest</div>
            </q-card-section>
            <q-card-section>
              <JsonEditor
                :theme="theme"
                v-model="formData.manifest"
                height="500px"
                @update:model-value="onFormChange"
              />
            </q-card-section>
          </q-card>
        </div>
      </div>
    </section>

    <section v-if="validationErrors.length" class="page-section q-mt-lg">
      <div class="text-subtitle1 text-weight-bold text-negative q-mb-sm">Validation Errors:</div>
      <ul class="validation-errors">
        <li v-for="(error, index) in validationErrors" :key="index" class="text-negative">
          {{ error }}
        </li>
      </ul>
    </section>

    <div class="buttons row q-mt-lg">
      <div class="col-12 flex q-gutter-sm">
        <q-btn
          v-if="!isValidated"
          color="primary"
          label="Validate"
          no-caps
          @click="validateForm"
          :loading="validating"
          :disabled="!isDirty || disableEditMessage"
        >
          <q-tooltip style="z-index: 99999" v-if="disableEditMessage">{{ disableEditMessage }}</q-tooltip>
        </q-btn>
        <q-btn
          v-else
          color="positive"
          :label="isEditMode ? 'Update' : 'Create'"
          no-caps
          @click="sendData"
          :loading="saving"
        />
        <q-btn flat no-caps label="Cancel" @click="$router.push('/infra-service-configs')" />
      </div>
    </div>
  </q-page>
</template>

<script>
import { isEqual } from 'lodash';
import { validateWorkFlow, createWorkFlow } from '../registrations/api/api';
import { getInfraServiceConfig } from './api/api';
import JsonEditor from './components/JsonEditor.vue';

export default {
  name: 'InfraServiceConfigPage',
  components: {
    JsonEditor,
  },
  data() {
    return {
      theme: 'vs-dark',
      formData: {
        apmid: undefined,
        status: undefined,
        manifest: '{}',
      },
      originalFormData: null,
      isValidated: false,
      validating: false,
      saving: false,
      validationErrors: [],
      disableEditMessage: undefined,
    };
  },
  computed: {
    apmId() {
      return this.$route.params.id;
    },
    isEditMode() {
      return !!this.apmId && this.apmId !== 'new';
    },
    isDirty() {
      if (!this.originalFormData) return false;
      return !isEqual(this.formData, this.originalFormData);
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.loadSchema();
    } else {
      this.originalFormData = JSON.parse(JSON.stringify(this.formData));
    }
  },
  methods: {
    onFormChange() {
      this.isValidated = false;
      this.validationErrors = [];
    },
    validateJsonFields() {
      const errors = [];

      try {
        JSON.parse(this.formData.manifest);
      } catch (e) {
        errors.push('Manifest: Invalid JSON syntax');
      }

      return errors;
    },
    buildPayload() {
      const payload = {
        status: this.formData.status,
        manifest: JSON.parse(this.formData.manifest),
      };

      if (this.isEditMode) {
        payload.uuid = this.formData.uuid;
      } else {
        payload.apmid = this.formData.apmid;
      }

      return payload;
    },
    async validateForm() {
      const isFormValid = await this.$refs.form.validate();
      if (!isFormValid) {
        this.$notify('negative', 'Error', 'Please fill in all required fields');
        return;
      }

      const jsonErrors = this.validateJsonFields();
      if (jsonErrors.length) {
        this.validationErrors = jsonErrors;
        this.$notify('negative', 'Error', 'Invalid JSON in schema field');
        return;
      }

      this.validating = true;
      this.validationErrors = [];

      try {
        const payload = this.buildPayload();
        const response = await validateWorkFlow({
          inputVariables: payload,
          workflowId: this.isEditMode ? 'engprt.update-infra-service-config' : 'engprt.create-infra-service-config',
        });

        if (response.valid || response.success || !response.errors?.length) {
          this.isValidated = true;
          this.$notify('positive', 'Success', 'Validation successful. You can now save the schema.');
        } else {
          this.validationErrors = response.errors || [response.message || 'Validation failed'];
          this.$notify('negative', 'Validation Failed', 'Please fix the errors and try again');
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Validation failed';
        this.validationErrors = Array.isArray(errorMessage) ? errorMessage : [errorMessage];
        this.$notify('negative', 'Error', errorMessage);
      } finally {
        this.validating = false;
      }
    },
    async sendData() {
      if (!this.isValidated) {
        this.$notify('warning', 'Warning', 'Please validate the form first');
        return;
      }

      this.saving = true;

      try {
        const payload = this.buildPayload();
        await createWorkFlow({
          inputVariables: payload,
          workflowId: this.isEditMode ? 'engprt.update-infra-service-config' : 'engprt.create-infra-service-config',
        });

        this.$notify(
          'positive',
          'Success',
          `Infra Service Config ${this.isEditMode ? 'updated' : 'created'} successfully`,
        );
        this.$router.push('/infra-service-configs');
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to save schema';
        this.$notify('negative', 'Error', errorMessage);
      } finally {
        this.saving = false;
      }
    },
    async loadSchema() {
      this.$showLoading();
      try {
        const response = await getInfraServiceConfig(this.apmId);
        this.formData = {
          apmid: response.apmid,
          uuid: response.uuid,
          status: response.status,
          manifest: JSON.stringify(response.manifest, null, 2),
        };
        this.originalFormData = JSON.parse(JSON.stringify(this.formData));

        const { uuid, status, manifest } = response;
        const data = {
          workflowId: 'engprt.update-infra-service-config',
          inputVariables: {
            uuid,
            status,
            manifest,
          },
        };
        await validateWorkFlow(data).catch((res) => {
          this.disableEditMessage = res.message;
        });
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.$hideLoading();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.page-section {
  background-color: white;
  border-radius: 12px;
  padding: 16px;
}

.validation-errors {
  margin: 0;
  padding-left: 20px;

  li {
    margin-bottom: 4px;
  }
}
</style>
